
































































import { Component, Mixins, Prop } from "vue-property-decorator";

import { IFilter } from "@/models/tables";
import RequestPromises from "@/mixins/requestPromises";

@Component
export default class DataFilter extends Mixins(RequestPromises) {
  public $refs!: {
    autocomplete: HTMLFormElement;
  };

  @Prop({ required: true })
  public filters!: IFilter[];

  @Prop({ required: true })
  public activeFilters!: IFilter[];

  @Prop({ default: () => [] })
  public preDefinedFilters!: IFilter[];

  @Prop({ default: "_action.add_filter" })
  public btnAddLabel!: string;

  @Prop({ default: false })
  public borderless!: boolean;

  @Prop({ default: false })
  public disabled!: boolean;

  @Prop({ default: true })
  public showRelative!: boolean;

  public name = "";
  public autocompleteFocused = false;
  public selectedFilter: IFilter = {} as IFilter;
  public focusedFilter: IFilter = {} as IFilter;

  // computed
  public get isSelectedFilterEmpty(): boolean {
    return this.$_.isEmpty(this.selectedFilter);
  }

  // computed
  public get notUsedFilters(): any {
    return this.filters?.filter(filter => {
      const isPredefinedFilter = this.$_.some(this.preDefinedFilters, [
        "key",
        filter.key
      ]);
      if (isPredefinedFilter) {
        return false;
      }

      return filter.label
        ? filter.label
            .toString()
            .toLowerCase()
            .indexOf(this.name.toLowerCase()) !== -1 &&
            !this.$_.some(this.activeFilters, ["key", filter.key])
        : "";
    });
  }

  // computed
  public get showAddFilter() {
    return (
      !this.isSelectedFilterEmpty &&
      !this.$_.some(this.activeFilters, ["key", this.selectedFilter.key])
    );
  }

  public beforeMount(): void {
    window.addEventListener("keyup", this.navigateThoughtFilters);
  }

  public beforeDestroy(): void {
    window.removeEventListener("keyup", this.navigateThoughtFilters);
  }

  public setSelectFilter(filter: any): void {
    this.$refs.autocomplete.isActive = false;
    if (filter === null) {
      return;
    }
    this.name = "";
    this.selectedFilter = this.$_.cloneDeep(filter);
  }

  public clearSelected(): void {
    this.selectedFilter = {} as IFilter;
  }

  public applyFilter(filter: IFilter) {
    this.clearSelected();
    if (this.$_.isEmpty(filter.value)) {
      return;
    }

    const { areActiveFiltersChanged, filters } = this.addOrUpdateFilter(
      this.$_.cloneDeep(this.activeFilters),
      this.$_.cloneDeep(filter)
    );

    if (areActiveFiltersChanged) {
      this.emitFiltersChanged(filters);
    }

    this.focusAutocomplete();
  }

  public onFocusAutocomplete(): void {
    this.clearSelected();
    this.focusedFilter = {} as IFilter;
    this.autocompleteFocused = true;
  }

  public onBlurAutocomplete(): void {
    this.autocompleteFocused = false;
  }

  public focusAutocomplete(): void {
    // the `blur` and `setTimeout` needed because otherwise the dropdown is not shown
    this.getAutocompleteInputEl().blur();
    setTimeout(() => {
      this.$refs.autocomplete?.focus();
      this.onFocusAutocomplete();
    }, 100);
  }

  public handleBackSpace() {
    if (
      this.autocompleteFocused &&
      this.activeFilters.length > 0 &&
      this.$_.isEmpty(this.focusedFilter) &&
      this.getAutocompleteInputEl().value.length === 0
    ) {
      this.getAutocompleteInputEl().blur();
      this.$refs.autocomplete.isActive = false;
      this.focusedFilter = this.$_.clone(
        this.$_.last(this.activeFilters)
      ) as IFilter;
    } else if (!this.$_.isEmpty(this.focusedFilter)) {
      this.removeFilter(this.focusedFilter);
    }
  }

  public handleArrowLeft() {
    if (
      this.autocompleteFocused &&
      this.activeFilters.length > 0 &&
      this.$_.isEmpty(this.focusedFilter) &&
      this.getAutocompleteInputEl().value.length === 0
    ) {
      this.getAutocompleteInputEl().blur();
      this.$refs.autocomplete.isActive = false;
      this.focusedFilter = this.$_.cloneDeep(
        this.$_.last(this.activeFilters)
      ) as IFilter;
    } else if (
      !this.$_.isEmpty(this.focusedFilter) &&
      this.activeFilters.length > 1
    ) {
      const index = this.$_.findIndex(this.activeFilters, [
        "key",
        this.$_.get(this.focusedFilter, "key")
      ]);
      if (index === 0) {
        this.focusedFilter = this.$_.cloneDeep(
          this.$_.last(this.activeFilters)
        ) as IFilter;
        return;
      }
      this.focusedFilter = this.$_.cloneDeep(this.activeFilters[index - 1]);
    }
  }

  public handleArrowRight() {
    if (this.$_.isEmpty(this.focusedFilter)) {
      return;
    }
    if (this.activeFilters.length > 1) {
      const index = this.$_.findIndex(this.activeFilters, [
        "key",
        this.$_.get(this.focusedFilter, "key")
      ]);
      if (index === this.activeFilters.length - 1) {
        this.focusAutocomplete();
        return;
      }
      this.focusedFilter = this.$_.cloneDeep(this.activeFilters[index + 1]);
    } else if (this.activeFilters.length === 1) {
      this.focusAutocomplete();
      return;
    }
  }

  public navigateThoughtFilters(event: any): any {
    switch (event.code) {
      case "Backspace":
        return this.handleBackSpace();

      case "Escape":
        return this.clearSelected();

      case "ArrowLeft":
        return this.handleArrowLeft();

      case "ArrowRight":
        return this.handleArrowRight();

      default:
        return;
    }
  }

  public addOrUpdateFilter(filters: IFilter[], filter: IFilter): any {
    const index = this.$_.findIndex(filters, ["key", filter.key]);
    let areActiveFiltersChanged = false;

    if (index !== -1) {
      if (
        filters[index].value !== filter.value ||
        filters[index].operator !== filter.operator
      ) {
        filters[index].value = filter.value;
        filters[index].operator = filter.operator;
        areActiveFiltersChanged = true;
      }
    } else {
      areActiveFiltersChanged = true;
      filters.push(filter);
    }

    return { areActiveFiltersChanged, filters };
  }

  public getAutocompleteInputEl(): any {
    return this.$refs.autocomplete.$el.querySelector("input");
  }

  public removeFilter(filter: any): void {
    const filters = this.$_.cloneDeep(this.activeFilters);
    const index = this.$_.findIndex(filters, ["key", filter.key]);
    filters.splice(index, 1);
    if (this.$_.isEqual(this.focusedFilter, filter)) {
      this.focusedFilter = {} as IFilter;
    }
    this.emitFiltersChanged(filters);
    if (!this.isSelectedFilterEmpty && filter.key === this.selectedFilter.key) {
      this.clearSelected();
    }
    this.focusAutocomplete();
  }

  public emitFiltersChanged(filters: IFilter[]): void {
    this.$emit("filtersChanged", filters);
  }
}
